// Renders a laid-out document host (docx-preview native page mode, already sanitized + geometry-fixed)
// into a jsPDF, one A4 page per `section.docx`. Deliberately SELF-CONTAINED (all helpers inlined, no
// module imports — the jsPDF instance and html2canvas are injected) so the SAME function drives
// production (utils/htmlToPdf.ts) and the throwaway fidelity/generality/multi-page harnesses via
// `.toString()` injection, guaranteeing the harness validates exactly what ships.
//
// THREE render paths, chosen per section by how many A4 pages it would occupy and whether it carries a
// repeatable header/footer/watermark:
//  1. SINGLE PAGE (a section that fits one A4 box): screenshot it whole -> one PDF page. Word-exact for
//     single-page docs and for each real page docx-preview split natively on an explicit/last-rendered
//     page break (each such section already carries its own header/footer/watermark).
//  2. BLIND SLICE (an over-tall section with NO header/footer/watermark to repeat — pure body overflow):
//     screenshot whole, slice DOWN across A4 pages. Unchanged legacy behaviour; also the fallback for
//     non-docx HTML (the FinalSettlement settlement letter, which has no `.docx-wrapper`).
//  3. BAND REPEAT (an over-tall section WITH a header/footer/watermark): docx-preview only paginates on
//     explicit/last-rendered breaks, so a doc that flows past one page through ordinary text overflow —
//     and carries NO break markers — comes back as ONE over-tall section with the header at the very top,
//     the footer at the very bottom, and the watermark centred once. Word repeats all three on EVERY
//     page. We reproduce that: capture the top-margin (header) strip and bottom-margin (footer) strip
//     once, slice the BODY across pages into the reserved middle band (so body text is never occluded),
//     and stamp header + footer + watermark onto every page. Body cuts snap to a low-ink row (a gap
//     between text lines) so no glyph row is sliced through at a seam.

import type { jsPDF } from 'jspdf';

type Html2Canvas = typeof import('html2canvas').default;

export async function renderDocxHostToPdf(pdf: jsPDF, host: HTMLElement, html2canvas: Html2Canvas): Promise<void> {
  const A4_CONTENT_WIDTH_PX = 794; // 210mm at 96dpi — also the exact native-mode `section.docx` width
  const pageWmm = pdf.internal.pageSize.getWidth();
  const pageHmm = pdf.internal.pageSize.getHeight();
  const pageHpx = A4_CONTENT_WIDTH_PX * (pageHmm / pageWmm); // 1123px — one A4 page at the section width
  const mmPerPx = pageWmm / A4_CONTENT_WIDTH_PX; // 210/794 — width maps 794px -> 210mm; height scales the same

  const captureCanvas = (
    el: HTMLElement, width: number, height?: number, backgroundColor: string | null = '#ffffff',
  ): Promise<HTMLCanvasElement> => html2canvas(el, {
    scale: 2, // ~192dpi — the biggest crispness lever for small Arabic glyphs
    useCORS: true,
    backgroundColor,
    logging: false,
    windowWidth: width,
    ...(height ? { width, height, windowHeight: height } : {}),
  });

  // A negative inline z-index is docx-preview's unambiguous marker for a Word "behind text" drawing
  // (watermark). Normal content is z-index auto/0/1.
  const isBehindText = (el: Element): boolean => {
    const zi = getComputedStyle(el as HTMLElement).zIndex;
    return !!zi && parseInt(zi, 10) < 0;
  };
  const behindTextDrawings = (sec: HTMLElement): HTMLElement[] =>
    (Array.from(sec.querySelectorAll('img, svg')) as HTMLElement[]).filter(isBehindText);

  // Does this section carry a header/footer/watermark that Word would repeat on every page? Only such
  // over-tall sections take the band-repeat path; a plain over-tall section (no bands) blind-slices.
  const hasRepeatableBands = (sec: HTMLElement): boolean => {
    const bandHasContent = (b: Element | null) =>
      !!b && (!!b.querySelector('img, svg') || (b.textContent || '').trim().length > 0);
    return bandHasContent(sec.querySelector(':scope > header'))
      || bandHasContent(sec.querySelector(':scope > footer'))
      || behindTextDrawings(sec).length > 0;
  };

  let pageStarted = false;
  const beginPage = () => {
    if (pageStarted) pdf.addPage();
    pageStarted = true;
  };

  // How many A4 pages the blind-slice path would produce for content of this rendered height (mirrors the
  // `while (heightLeft > 1)` loop below). imgHmm = cssHeight * mmPerPx.
  const blindPageCount = (imgHmm: number) => {
    let heightLeft = imgHmm, n = 0;
    while (heightLeft > 1) { n++; heightLeft -= pageHmm; }
    return n;
  };

  // Path 1/2: place a captured canvas full page-width, slicing it DOWN across as many A4 pages as its
  // height needs. A one-page canvas (imgHmm <= pageHmm) yields exactly one page (1mm tolerance drops the
  // sub-pixel sliver rounding can leave). Legacy behaviour for single-page sections, plain over-tall
  // sections, and the non-docx fallback.
  const addCanvasPaged = (canvas: HTMLCanvasElement) => {
    const imgData = canvas.toDataURL('image/png'); // PNG keeps text crisp (JPEG blurs glyphs)
    const imgHmm = (canvas.height * pageWmm) / canvas.width;
    let heightLeft = imgHmm;
    let position = 0;
    while (heightLeft > 1) {
      beginPage();
      pdf.addImage(imgData, 'PNG', 0, position, pageWmm, imgHmm, undefined, 'FAST');
      heightLeft -= pageHmm;
      position -= pageHmm;
    }
  };

  // Path 3: repeat the header/footer/watermark on every page of an over-tall (break-less) section.
  const addSectionBanded = async (sec: HTMLElement) => {
    const secRect = sec.getBoundingClientRect();
    const secWpx = Math.ceil(secRect.width);
    const secHpx = secRect.height;
    const cs = getComputedStyle(sec);
    const padTop = parseFloat(cs.paddingTop) || 0;
    const padBot = parseFloat(cs.paddingBottom) || 0;

    // Reserved bands = the page margins, extended if the header/footer content is taller than the margin
    // (so the whole band is preserved and the body starts exactly where Word puts it, beneath the header).
    const hdr = sec.querySelector(':scope > header') as HTMLElement | null;
    const ftr = sec.querySelector(':scope > footer') as HTMLElement | null;
    const hdrBottom = hdr ? hdr.getBoundingClientRect().bottom - secRect.top : padTop;
    const ftrTop = ftr ? ftr.getBoundingClientRect().top - secRect.top : secHpx - padBot;
    const topReserve = Math.round(Math.max(padTop, Math.min(hdrBottom, secHpx * 0.5)));
    const bottomReserve = Math.round(Math.max(padBot, Math.min(secHpx - ftrTop, secHpx * 0.5)));

    // Body area per page = one Word text column height (A4 minus the two margins). If the header/footer
    // bands are so tall they'd leave less than ~45% of the page for body (e.g. a giant full-page-band
    // letterhead image top AND bottom), repeating them per page would explode the page count — fall back
    // to the plain blind slice (header on page 1, footer on the last page) rather than paginate absurdly.
    const bodyAreaPx = pageHpx - topReserve - bottomReserve;
    if (bodyAreaPx < pageHpx * 0.45) {
      addCanvasPaged(await captureCanvas(sec, secWpx, Math.ceil(secHpx)));
      return;
    }

    // Capture each watermark on its own (transparent bg preserves its faint 0.12 alpha), THEN hide them so
    // the section canvas — from which the header/footer strips and the body are cropped — is watermark-free
    // (otherwise the page holding the watermark's centre would show it twice: baked + re-stamped). We clone
    // the drawing into a plain off-screen wrapper before capturing: html2canvas throws "Unable to find
    // element in cloned iframe" if an <svg> (docx-preview renders VML watermarks as inline SVG) is passed
    // as the capture root, and the clone lets us neutralise its positioning transform + re-apply the faint
    // opacity (which comes from a `.docx`-scoped rule that would not match outside the section).
    const watermarks = behindTextDrawings(sec);
    const wmShots: { png: string; wpx: number; hpx: number }[] = [];
    for (const wm of watermarks) {
      const r = wm.getBoundingClientRect();
      if (r.width < 1 || r.height < 1) continue;
      const w = Math.ceil(r.width), h = Math.ceil(r.height);
      const holder = document.createElement('div');
      holder.style.cssText = `position:fixed;left:-20000px;top:0;width:${w}px;height:${h}px;overflow:hidden;`;
      const clone = wm.cloneNode(true) as HTMLElement;
      Object.assign(clone.style, {
        position: 'static', transform: 'none', margin: '0', inset: 'auto',
        width: `${w}px`, height: `${h}px`, opacity: getComputedStyle(wm).opacity || '1',
      } as Partial<CSSStyleDeclaration>);
      holder.appendChild(clone);
      document.body.appendChild(holder);
      try {
        const c = await captureCanvas(holder, w, h, null);
        wmShots.push({ png: c.toDataURL('image/png'), wpx: r.width, hpx: r.height });
      } finally {
        document.body.removeChild(holder);
      }
    }
    const prevDisplay = watermarks.map((w) => w.style.display);
    watermarks.forEach((w) => { w.style.display = 'none'; });
    const full = await captureCanvas(sec, secWpx, Math.ceil(secHpx));
    watermarks.forEach((w, i) => { w.style.display = prevDisplay[i]; });

    const S = full.height / secHpx; // canvas px per CSS px (~2)
    const cropStrip = (topCssPx: number, hCssPx: number): string => {
      const sh = Math.max(1, Math.round(hCssPx * S));
      const c = document.createElement('canvas');
      c.width = full.width; c.height = sh;
      c.getContext('2d')!.drawImage(full, 0, Math.round(topCssPx * S), full.width, sh, 0, 0, full.width, sh);
      return c.toDataURL('image/png');
    };
    const headerPng = cropStrip(0, topReserve);
    const footerPng = cropStrip(secHpx - bottomReserve, bottomReserve);

    // Body region canvas (between the reserved bands) — the only part we slice across pages.
    const bodyTopPx = topReserve, bodyBotPx = secHpx - bottomReserve;
    const bodyCanvas = document.createElement('canvas');
    bodyCanvas.width = full.width;
    bodyCanvas.height = Math.max(1, Math.round((bodyBotPx - bodyTopPx) * S));
    bodyCanvas.getContext('2d')!.drawImage(full, 0, Math.round(bodyTopPx * S), full.width, bodyCanvas.height, 0, 0, full.width, bodyCanvas.height);

    // Per-row ink count so cuts can snap to a gap between text lines (no glyph row sliced at a seam).
    const bctx = bodyCanvas.getContext('2d')!;
    const bimg = bctx.getImageData(0, 0, bodyCanvas.width, bodyCanvas.height);
    const rowInk = new Array(bodyCanvas.height).fill(0);
    for (let y = 0; y < bodyCanvas.height; y++) {
      let n = 0; const base = y * bodyCanvas.width * 4;
      for (let x = 0; x < bodyCanvas.width; x++) {
        const i = base + x * 4;
        const lum = 0.299 * bimg.data[i] + 0.587 * bimg.data[i + 1] + 0.114 * bimg.data[i + 2];
        if (lum < 180) n++;
      }
      rowInk[y] = n;
    }

    const bodyAreaCanvasPx = Math.floor(bodyAreaPx * S);
    const snapWindow = Math.round(30 * S); // ~one text line — search this far above the hard target for a gap
    const cuts = [0];
    let start = 0;
    while (bodyCanvas.height - start > bodyAreaCanvasPx) {
      const target = start + bodyAreaCanvasPx;
      let best = target, bestInk = Infinity;
      for (let y = Math.max(start + 1, target - snapWindow); y <= target; y++) {
        if (rowInk[y] <= bestInk) { bestInk = rowInk[y]; best = y; } // prefer the lowest, latest gap
      }
      if (best <= start) best = target; // safety — never regress
      cuts.push(best);
      start = best;
    }
    cuts.push(bodyCanvas.height);

    const topReserveMm = topReserve * mmPerPx;
    const bottomReserveMm = bottomReserve * mmPerPx;
    const padTopMm = padTop * mmPerPx, padBotMm = padBot * mmPerPx;
    const wmCenterYmm = padTopMm + (pageHmm - padTopMm - padBotMm) / 2; // Word centres the watermark on the margin box

    // Gap 2: renumber PAGE / NUMPAGES fields (tagged at convert time in docxToHtml) per page. The band-repeat
    // path screenshots the header/footer strip ONCE, freezing the field at its cached value ("1 of N" on every
    // page); we redraw the correct number on each page. Only fields inside a reserved (repeated) strip can be
    // renumbered — a field in the body flow moves with the body and is left untouched. NUMPAGES = the real band
    // page count, not docx-preview's stale cache. (PDF-only: the wizard preview shows one continuous section and
    // cannot paginate a markerless doc, so its footer keeps the single cached value — an accepted residual.)
    const totalPages = cuts.length - 1;
    const parseRgb = (c: string): [number, number, number] => {
      const m = /rgba?\((\d+)[,\s]+(\d+)[,\s]+(\d+)/.exec(c);
      return m ? [+m[1], +m[2], +m[3]] : [0, 0, 0];
    };
    const pageFields = (Array.from(sec.querySelectorAll('[data-mzf-pagefield]')) as HTMLElement[]).map((el) => {
      const r = el.getBoundingClientRect();
      const relTop = r.top - secRect.top;
      const cs2 = getComputedStyle(el);
      const midY = relTop + r.height / 2;
      return {
        kind: el.getAttribute('data-mzf-pagefield') === 'NUMPAGES' ? 'NUMPAGES' : 'PAGE',
        relLeft: r.left - secRect.left, relTop, w: r.width, h: r.height,
        inHeader: midY < topReserve, inFooter: midY >= secHpx - bottomReserve,
        fontPt: (parseFloat(cs2.fontSize) || 12) * (72 / 96),
        rgb: parseRgb(cs2.color),
        serif: /times|serif|georgia|garamond/i.test(cs2.fontFamily),
      };
    }).filter((f) => f.inHeader || f.inFooter);

    for (let p = 0; p < cuts.length - 1; p++) {
      const c0 = cuts[p], c1 = cuts[p + 1];
      const chunkH = c1 - c0;
      const chunk = document.createElement('canvas');
      chunk.width = bodyCanvas.width; chunk.height = chunkH;
      chunk.getContext('2d')!.drawImage(bodyCanvas, 0, c0, bodyCanvas.width, chunkH, 0, 0, bodyCanvas.width, chunkH);
      const chunkHmm = (chunkH / S) * mmPerPx;

      beginPage();
      pdf.addImage(headerPng, 'PNG', 0, 0, pageWmm, topReserveMm, undefined, 'FAST');
      pdf.addImage(chunk.toDataURL('image/png'), 'PNG', 0, topReserveMm, pageWmm, chunkHmm, undefined, 'FAST');
      pdf.addImage(footerPng, 'PNG', 0, pageHmm - bottomReserveMm, pageWmm, bottomReserveMm, undefined, 'FAST');
      for (const w of wmShots) {
        const wmm = w.wpx * mmPerPx, hmm = w.hpx * mmPerPx;
        pdf.addImage(w.png, 'PNG', pageWmm / 2 - wmm / 2, wmCenterYmm - hmm / 2, wmm, hmm, undefined, 'FAST');
      }
      // Overlay the correct page number LAST (on top of the re-stamped strip): erase the frozen digit with a
      // white patch, then draw the right value centred on the field. Header strip is at page y=0; footer strip
      // at pageHmm-bottomReserveMm — map the field's section-relative top into whichever strip holds it.
      for (const f of pageFields) {
        const value = f.kind === 'NUMPAGES' ? String(totalPages) : String(p + 1);
        const fieldTopMm = f.inHeader
          ? f.relTop * mmPerPx
          : (pageHmm - bottomReserveMm) + (f.relTop - (secHpx - bottomReserve)) * mmPerPx;
        const centerXmm = (f.relLeft + f.w / 2) * mmPerPx;
        pdf.setFont(f.serif ? 'times' : 'helvetica', 'normal');
        pdf.setFontSize(f.fontPt);
        const patchWmm = Math.max(f.w * mmPerPx, pdf.getTextWidth(value)) + 1;
        pdf.setFillColor(255, 255, 255);
        pdf.rect(centerXmm - patchWmm / 2, fieldTopMm - 0.4, patchWmm, f.h * mmPerPx + 0.8, 'F');
        pdf.setTextColor(f.rgb[0], f.rgb[1], f.rgb[2]);
        pdf.text(value, centerXmm, fieldTopMm, { align: 'center', baseline: 'top' });
      }
    }
  };

  // NATIVE PAGE MODE: one `section.docx` A4 box per page. Each section is single-page (screenshot whole),
  // a plain over-tall overflow (blind slice), or an over-tall section with bands to repeat (band repeat).
  const sections = Array.from(host.querySelectorAll('section.docx')) as HTMLElement[];
  if (host.querySelector('.docx-wrapper') && sections.length > 0) {
    for (const sec of sections) {
      const rect = sec.getBoundingClientRect();
      const imgHmm = rect.height * mmPerPx; // == (capturedCanvas.height * pageWmm) / capturedCanvas.width (scale cancels)
      if (blindPageCount(imgHmm) >= 2 && hasRepeatableBands(sec)) {
        await addSectionBanded(sec);
      } else {
        addCanvasPaged(await captureCanvas(sec, Math.ceil(rect.width), Math.ceil(rect.height)));
      }
    }
  } else {
    // Fallback for non-docx HTML (e.g. the FinalSettlement settlement letter): one tall canvas of the
    // whole host, sliced across A4 pages.
    addCanvasPaged(await captureCanvas(host, A4_CONTENT_WIDTH_PX));
  }
}
